// src/components/layout/AppDrawer.tsx
import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  Toolbar,
  IconButton,
  Collapse,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate, useLocation } from "react-router-dom";
import { DRAWER_WIDTH, DRAWER_WIDTH_MINI } from "./types";
import { useFilteredNavigation } from "../../hooks/useFilteredNavigation";

interface AppDrawerProps {
  open: boolean;
  onClose: () => void;
  miniMode: boolean;
  toggleMiniMode: () => void;
}


export function AppDrawer({
  open,
  onClose,
  miniMode,
  toggleMiniMode,
}: AppDrawerProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { navigationItems } = useFilteredNavigation();
  const menuItems = navigationItems;

  // State for tracking which menu categories are expanded
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>(
    () => {
      // Initialize expanded state based on current path
      const currentPath = location.pathname;
      const initialState: Record<string, boolean> = {};

      menuItems.forEach((item) => {
        if (item.subItems) {
          // Check if any subitem matches the current path
          const isActive = item.subItems.some(
            (subItem) =>
              currentPath === subItem.path ||
              currentPath.startsWith(subItem.path)
          );
          initialState[item.text] = isActive;
        }
      });

      return initialState;
    }
  );

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const toggleMenuExpand = (menuText: string) => {
    if (miniMode && !isMobile) {
      // When in mini mode, first expand the drawer when clicking main item
      toggleMiniMode();
      // Then expand the submenu
      setExpandedMenus((prev) => ({
        ...prev,
        [menuText]: true,
      }));
    } else {
      setExpandedMenus((prev) => ({
        ...prev,
        [menuText]: !prev[menuText],
      }));
    }
  };

  // Check if current route matches a path
  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };

  const drawerContent = (
    <>
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          minHeight: "64px !important",
          backgroundColor: "primary.main",
          color: "white",
        }}
      >
        {!isMobile && (
          <IconButton onClick={toggleMiniMode} sx={{ color: "white" }}>
            {miniMode ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
      </Toolbar>
      <Box sx={{ overflow: "auto" }}>
        <List>
          {menuItems.map((item) => (
            <React.Fragment >
              <ListItem disablePadding key={item.text}>
                <ListItemButton
                  onClick={() => {
                    if (item.subItems) {
                      toggleMenuExpand(item.text);
                    } else {
                      handleNavigation(item.path);
                    }
                  }}
                  sx={{
                    minHeight: 48,
                    justifyContent:
                      miniMode && !isMobile ? "center" : "initial",
                    px: 2.5,
                    backgroundColor:
                      isActive(item.path) &&
                      !(item.subItems && expandedMenus[item.text])
                        ? "secondary.light"
                        : "transparent",
                    "&:hover": {
                      backgroundColor: "secondary.light",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: miniMode && !isMobile ? 0 : 3,
                      justifyContent: "center",
                      color: isActive(item.path)
                        ? "primary.main"
                        : "text.primary",
                    }}
                  >
                    {<item.icon />}
                  </ListItemIcon>
                  {(!miniMode || isMobile) && (
                    <>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: isActive(item.path) ? 700 : 500,
                            color: isActive(item.path)
                              ? "primary.main"
                              : "text.primary",
                          },
                        }}
                      />
                      {item.subItems &&
                        (expandedMenus[item.text] ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        ))}
                    </>
                  )}
                </ListItemButton>
              </ListItem>

              {/* Submenu items */}
              {item.subItems && (
                <Collapse
                  in={expandedMenus[item.text]}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => (
                      <ListItemButton
                        key={subItem.path}
                        onClick={() => handleNavigation(subItem.path)}
                        sx={{
                          pl: miniMode && !isMobile ? 2.5 : 4,
                          py: 1,
                          minHeight: 36,
                          backgroundColor: isActive(subItem.path)
                            ? "secondary.light"
                            : "transparent",
                          "&:hover": {
                            backgroundColor: "secondary.light",
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: 2,
                            color: isActive(subItem.path)
                              ? "primary.main"
                              : "text.secondary",
                          }}
                        >
                          {<subItem.icon fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText
                          primary={subItem.text}
                          primaryTypographyProps={{
                            variant: "body2",
                            sx: {
                              fontWeight: isActive(subItem.path) ? 600 : 400,
                              color: isActive(subItem.path)
                                ? "primary.main"
                                : "text.secondary",
                            },
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: DRAWER_WIDTH,
            backgroundColor: "background.paper",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: "none", sm: "block" },
        "& .MuiDrawer-paper": {
          width: miniMode ? DRAWER_WIDTH_MINI : DRAWER_WIDTH,
          backgroundColor: "background.paper",
          borderRight: "1px solid rgba(0,0,0,0.12)",
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: "hidden",
        },
      }}
      open
    >
      {drawerContent}
    </Drawer>
  );
}
